import { NodeViewWrapper } from '@tiptap/react';
import { ExternalLayerItem } from 'components/MapPage/CorporateViewTabs/LayersTab/ExternalLayerItem';
import { InternalLayerItem } from 'components/MapPage/CorporateViewTabs/LayersTab/InternalLayerItem';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useListLayerNodes } from 'hooks/layerNode/useListLayerNodes';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import type { InternalLayer } from 'hooks/internalLayer/types';
import type { ReportLayerType } from './reportMentionTypes';

type ReportLayerEmbedProps = {
  layerId: string;
  layerType?: ReportLayerType;
  label?: string;
};

export function ReportLayerEmbedView({
  layerId,
  layerType,
  label,
}: ReportLayerEmbedProps) {
  const mapCtx = useOptionalUscgMapContext();
  const groupGuid = useGroupGuid();
  const { data: layerTree } = useListLayerNodes(
    { filter: { groupGuid: groupGuid ?? '' } },
    { enabled: !!groupGuid }
  );

  let resolved: {
    layer: ExternalLayer | InternalLayer;
    isExternal: boolean;
  } | null = null;
  for (const group of layerTree?.data ?? []) {
    for (const item of group.items) {
      const layer = item.layer;
      if (String(layer?.id) !== layerId) continue;
      resolved = { layer, isExternal: 'serviceUrl' in layer };
      break;
    }
    if (resolved) break;
  }

  if (!resolved) {
    return (
      <div className="tw:rounded tw:border tw:border-border tw:px-3 tw:py-2 tw:text-sm tw:text-muted-foreground">
        {label || 'Map layer'}
      </div>
    );
  }

  if (!mapCtx) {
    return (
      <div className="tw:rounded tw:border tw:border-border tw:bg-card tw:px-3 tw:py-2 tw:text-sm">
        <strong>{resolved.layer.name || label}</strong>
        <span className="tw:ml-2 tw:text-muted-foreground">
          {resolved.isExternal || layerType === 'external'
            ? 'External layer'
            : 'Internal layer'}
        </span>
      </div>
    );
  }

  return (
    <ul className="tw:mb-0 tw:list-none tw:p-0">
      {resolved.isExternal ? (
        <ExternalLayerItem
          layer={resolved.layer as ExternalLayer}
          onZoomToGraphic={() => {}}
        />
      ) : (
        <InternalLayerItem layer={resolved.layer as InternalLayer} />
      )}
    </ul>
  );
}

export function ReportLayerEmbedNodeView(props: {
  node: { attrs: Record<string, unknown> };
}) {
  const layerId = String(props.node.attrs.layerId || '');
  const layerType =
    props.node.attrs.layerType === 'external' ? 'external' : 'internal';
  const label = String(props.node.attrs.label || '');
  return (
    <NodeViewWrapper className="report-layer-embed" data-drag-handle>
      <ReportLayerEmbedView
        layerId={layerId}
        layerType={layerType}
        label={label}
      />
    </NodeViewWrapper>
  );
}
